export {
  ViewableDateShort,
  ViewableDateShortAus,
  ViewableDate,
  ViewableDateAus,
  ViewableDateLong,
};

/**
 * Output human readable date without time information. Provides consistent styling for all FE Dates.
 * @param input {Date | String | Number} input object
 */
const ViewableDateShort = (
  input: undefined | Date | number,
  withSeconds?: boolean,
): string => {
  if (input == undefined) return "";

  if (input instanceof Date) {
    // convert to format. YYYY-MM-DD
    const calendarDate = `${input.getFullYear()}-${input.getMonth() + 1}-${input.getDate()}`;
    return calendarDate;
  }
  // assume Number is epoch in MS.
  return ViewableDateShort(new Date(input as number), withSeconds);
};

/**
 * Output human readable date without time information. Provides consistent styling for all FE Dates.
 * @param input {Date | String | Number} input object
 */
const ViewableDateShortAus = (
  input: undefined | Date | number,
  withSeconds?: boolean,
): string => {
  if (input == undefined) return "";

  if (input instanceof Date) {
    // convert to format. DD-MM-YYYY
    const calendarDate = `${input.getDate()}-${input.getMonth() + 1}-${input.getFullYear()}`;
    return calendarDate;
  }
  // assume Number is epoch in MS.
  return ViewableDateShortAus(new Date(input as number), withSeconds);
};

/**
 * Output human readable date with time information. Provides consistent styling for all FE Dates.
 * @param input {Date | String | Number} input object
 * @param withSeconds {boolean | undefined} include seconds in timer
 */
const ViewableDate = (
  input: undefined | Date | number,
  withSeconds?: boolean,
): string => {
  if (input == undefined) return "";

  if (input instanceof Date) {
    // convert to format. YYYY-MM-DD HH:MM (optionally with .SS suffix)
    const calendarDate = `${input.getFullYear()}-${input.getMonth() + 1}-${input.getDate()}`;
    const time = `${padTime(input.getHours())}:${padTime(input.getMinutes())}`;

    return (
      calendarDate +
      " " +
      time +
      (withSeconds ? `.${padTime(input.getSeconds())}` : "")
    );
  }
  // assume Number is epoch in MS.
  return ViewableDate(new Date(input as number), withSeconds);
};

/**
 * Output human readable date with time information. Provides consistent styling for all FE Dates.
 * @param input {Date | String | Number} input object
 * @param withSeconds {boolean | undefined} include seconds in timer
 */
const ViewableDateAus = (
  input: undefined | Date | number,
  withSeconds?: boolean,
): string => {
  if (input == undefined) return "";

  if (input instanceof Date) {
    // convert to format. DD-MM-YYYY
    const calendarDate = `${input.getDate()}-${input.getMonth() + 1}-${input.getFullYear()}`;
    const time = `${padTime(input.getHours())}:${padTime(input.getMinutes())}`;

    return (
      calendarDate +
      " " +
      time +
      (withSeconds ? `.${padTime(input.getSeconds())}` : "")
    );
  }
  // assume Number is epoch in MS.
  return ViewableDateAus(new Date(input as number), withSeconds);
};

/**
 * Output human readable date with time information like spoken word. Provides consistent styling for all FE Dates.
 * @param input {Date | String | number} input object
 * @param withSeconds {boolean | undefined} include seconds in timer
 */
const ViewableDateLong = (
  input: undefined | Date | number,
  withSeconds?: boolean,
): string => {
  if (input == undefined) return "";

  if (input instanceof Date) {
    // convert to format. DD/MM/YYY HH:MM (optionally with .SS suffix)
    const calendarDate = `${input.getDate()} ${input.toLocaleString("default", { month: "long" })} ${input.getFullYear()}`;
    const time = `${padTime(input.getHours())}:${padTime(input.getMinutes())}`;
    return (
      calendarDate +
      " " +
      time +
      (withSeconds ? `.${padTime(input.getSeconds())}` : "")
    );
  }

  // assume Number is epoch in MS.
  return ViewableDateLong(new Date(input as number), withSeconds);
};

function padTime(input: number): string {
  return input < 10 ? "0" + input : input + "";
}
